class FloydProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.productGallery = this.querySelector('floyd-prestige-product-gallery');
    this.previousButton = this.querySelector('[data-floyd-gallery-previous]');
    this.nextButton = this.querySelector('[data-floyd-gallery-next]');
    this.variantMediaMap = this.readVariantMediaMap(this);

    this.prepareVariantMediaOrder(
      this.productGallery,
      this.selectedOptionValues,
      this.variantMediaMap,
      this.dataset.selectedVariantId,
    );
    this.setupMainMediaDrag();
    this.setupDesktopHoverZoom();
    this.setupThumbnailDrag();
    this.initializeMedia();

    this.addEventListener('click', (event) => this.onMediaNavigationClick(event), {
      capture: true,
      signal: this.abortController.signal,
    });

    this.previousButton?.addEventListener('click', () => this.move(-1), {
      signal: this.abortController.signal,
    });
    this.nextButton?.addEventListener('click', () => this.move(1), {
      signal: this.abortController.signal,
    });
    window.addEventListener('resize', () => this.updateNavigation(), {
      signal: this.abortController.signal,
    });

    this.bindProductGalleryEvents();

    const productForm = document.forms[this.dataset.productFormId];
    productForm?.addEventListener('product:rerender', (event) => {
      const incomingRoot = event.detail.htmlFragment.querySelector(
        `floyd-product-gallery[data-product-form-id="${this.dataset.productFormId}"]`,
      );

      if (incomingRoot) {
        const incomingGallery = incomingRoot.querySelector('floyd-prestige-product-gallery');
        const incomingMediaMap = this.readVariantMediaMap(incomingRoot);

        if (incomingGallery) {
          delete this.dataset.floydMediaReady;
          this.dataset.selectedVariantId = incomingRoot.dataset.selectedVariantId;
          this.prepareVariantMediaOrder(
            incomingGallery,
            this.selectedOptionValues,
            incomingMediaMap,
            incomingRoot.dataset.selectedVariantId,
          );
          this.productGallery.replaceWith(incomingGallery);
          this.productGallery = incomingGallery;
          this.variantMediaMap = incomingMediaMap;
          this.bindProductGalleryEvents();
          this.setupMainMediaDrag();
          this.setupDesktopHoverZoom();
          this.setupThumbnailDrag();
          this.initializeNavigation();
        }
      }

      this.initializeMedia();
    }, { signal: this.abortController.signal });
    productForm?.addEventListener('variant:change', () => {
      requestAnimationFrame(() => this.selectFirstVisibleMedia());
    }, { signal: this.abortController.signal });


    this.setup3dViewer();
    this.initializeNavigation();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.navigationSelectionMediaId = null;
    this.abortController = null;
  }

  get visibleThumbnails() {
    return Array.from(this.querySelectorAll('.product-gallery__thumbnail:not([hidden])'));
  }

  get visibleMedia() {
    return Array.from(
      this.productGallery?.querySelectorAll('.product-gallery__carousel > .product-gallery__media:not([hidden])') || [],
    );
  }

  get selectedThumbnailIndex() {
    return this.visibleThumbnails.findIndex((thumbnail) => thumbnail.getAttribute('aria-current') === 'true');
  }

  get selectedOptionValues() {
    const productForm = document.forms[this.dataset.productFormId];

    return Array.from(productForm?.elements || [])
      .filter((element) => element.matches('input[data-option-position]:checked'))
      .sort((first, second) => Number(first.dataset.optionPosition) - Number(second.dataset.optionPosition))
      .map((element) => element.labels?.[0]?.textContent?.trim() || element.value);
  }

  readVariantMediaMap(root) {
    try {
      return JSON.parse(root.querySelector('[data-floyd-variant-media-map]')?.textContent || 'null');
    } catch (error) {
      console.error('Unable to read Floyd variant media map.', error);
      return null;
    }
  }

  bindProductGalleryEvents() {
    const carousel = this.productGallery?.querySelector('.product-gallery__carousel');
    const thumbnailScroller = this.productGallery?.querySelector('.product-gallery__thumbnail-scroller');

    // Keep main-media swipes visually synchronized with the thumbnail strip.
    // Direct thumbnail selections wait for settle so they move only once.
    thumbnailScroller?.removeAttribute('align-selected');

    carousel?.addEventListener('carousel:change', (event) => {
      const mediaId = event.detail?.cell?.dataset.mediaId;

      this.activateMedia(mediaId);
      this.syncMediaNavigation(mediaId);
      if (this.navigationSelectionMediaId !== mediaId) this.alignSelectedThumbnail(mediaId);
      this.updateNavigation();
    }, {
      signal: this.abortController.signal,
    });
    carousel?.addEventListener('carousel:settle', (event) => {
      const mediaId = event.detail?.cell?.dataset.mediaId;

      this.syncMediaNavigation(mediaId);
      this.alignSelectedThumbnail(mediaId);
      this.navigationSelectionMediaId = null;
      this.updateNavigation();
    }, {
      signal: this.abortController.signal,
    });
  }

  onMediaNavigationClick(event) {
    const media = event.target.closest('.product-gallery__carousel .product-gallery__media');

    if (
      media?.dataset.mediaType === 'image'
      && event.target.closest('img')
      && window.matchMedia('(min-width: 1000px) and (pointer: fine)').matches
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (media && this.suppressMainMediaClick) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const navigationButton = event.target.closest(
      '.product-gallery__thumbnail, .page-dots > button[data-media-id]',
    );

    if (!navigationButton || !this.contains(navigationButton)) return;

    if (this.suppressThumbnailClick) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    const mediaId = navigationButton.dataset.mediaId;
    const targetIndex = this.visibleMedia.findIndex((media) => media.dataset.mediaId === mediaId);
    const carousel = this.productGallery?.querySelector('.product-gallery__carousel');

    if (!carousel || targetIndex < 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.navigationSelectionMediaId = mediaId;
    this.activateMedia(mediaId);
    carousel.select?.(targetIndex);
    this.syncMediaNavigation(mediaId);
    requestAnimationFrame(() => this.updateNavigation());
  }

  setupMainMediaDrag() {
    const carousel = this.productGallery?.querySelector('.product-gallery__carousel');

    if (!carousel || carousel.dataset.floydDragReady === 'true') return;

    carousel.dataset.floydDragReady = 'true';

    let startX = 0;
    let dragged = false;

    carousel.addEventListener('mousedown', (event) => {
      if (event.button !== 0) return;

      startX = event.clientX;
      dragged = false;
    }, { signal: this.abortController.signal });

    carousel.addEventListener('mousemove', (event) => {
      if (event.buttons !== 1) return;
      if (Math.abs(event.clientX - startX) > 4) dragged = true;
    }, { signal: this.abortController.signal });

    carousel.addEventListener('mouseup', () => {
      if (!dragged) return;

      this.suppressMainMediaClick = true;
      setTimeout(() => { this.suppressMainMediaClick = false; }, 0);
    }, { signal: this.abortController.signal });
  }

  setupDesktopHoverZoom() {
    const carousel = this.productGallery?.querySelector('.product-gallery__carousel');

    if (
      !carousel
      || carousel.dataset.floydHoverZoomReady === 'true'
      || !this.productGallery?.hasAttribute('allow-zoom')
    ) return;

    carousel.dataset.floydHoverZoomReady = 'true';

    const resetZoom = () => {
      carousel.querySelectorAll('.product-gallery__media.is-hover-zooming').forEach((media) => {
        media.classList.remove('is-hover-zooming');
        media.style.removeProperty('--floyd-hover-zoom-x');
        media.style.removeProperty('--floyd-hover-zoom-y');
      });
    };

    carousel.addEventListener('pointermove', (event) => {
      if (
        event.pointerType !== 'mouse'
        || event.buttons !== 0
        || !window.matchMedia('(min-width: 1000px) and (pointer: fine)').matches
      ) {
        resetZoom();
        return;
      }

      const media = event.target.closest('.product-gallery__media[data-media-type="image"]');
      if (!media || media.hidden) {
        resetZoom();
        return;
      }

      const bounds = media.getBoundingClientRect();
      const image = media.querySelector(':scope > img');
      const x = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1) * 100;
      const y = Math.min(Math.max((event.clientY - bounds.top) / bounds.height, 0), 1) * 100;

      if (image?.srcset) image.sizes = `${Math.ceil(bounds.width * 2)}px`;

      carousel.querySelectorAll('.product-gallery__media.is-hover-zooming').forEach((zoomedMedia) => {
        if (zoomedMedia !== media) zoomedMedia.classList.remove('is-hover-zooming');
      });
      media.style.setProperty('--floyd-hover-zoom-x', `${x}%`);
      media.style.setProperty('--floyd-hover-zoom-y', `${y}%`);
      media.classList.add('is-hover-zooming');
    }, { signal: this.abortController.signal });

    carousel.addEventListener('pointerleave', resetZoom, { signal: this.abortController.signal });
    carousel.addEventListener('mousedown', resetZoom, { signal: this.abortController.signal });
  }

  setupThumbnailDrag() {
    const scroller = this.productGallery?.querySelector('.product-gallery__thumbnail-scroller');

    if (!scroller || scroller.dataset.floydDragReady === 'true') return;

    scroller.dataset.floydDragReady = 'true';

    let navigationFrame = null;
    scroller.addEventListener('scroll', () => {
      if (navigationFrame !== null) return;

      navigationFrame = requestAnimationFrame(() => {
        navigationFrame = null;
        this.updateNavigation();
      });
    }, { passive: true, signal: this.abortController.signal });

    let pointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragged = false;

    scroller.addEventListener('dragstart', (event) => event.preventDefault(), {
      signal: this.abortController.signal,
    });

    scroller.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || event.pointerType === 'touch') return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = scroller.scrollLeft;
      dragged = false;
      scroller.classList.add('is-dragging');
      scroller.style.scrollSnapType = 'none';
    }, { signal: this.abortController.signal });

    scroller.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;

      const distance = event.clientX - startX;

      if (Math.abs(distance) > 4 && !dragged) {
        dragged = true;
        scroller.setPointerCapture(pointerId);
      }
      if (!dragged) return;

      event.preventDefault();
      scroller.scrollLeft = startScrollLeft - distance;
    }, { passive: false, signal: this.abortController.signal });

    const finishDrag = (event) => {
      if (event.pointerId !== pointerId) return;

      if (dragged) {
        this.suppressThumbnailClick = true;
        setTimeout(() => { this.suppressThumbnailClick = false; }, 0);
      }

      if (scroller.hasPointerCapture(pointerId)) scroller.releasePointerCapture(pointerId);
      pointerId = null;
      scroller.classList.remove('is-dragging');
      scroller.style.removeProperty('scroll-snap-type');
    };

    scroller.addEventListener('pointerup', finishDrag, { signal: this.abortController.signal });
    scroller.addEventListener('pointercancel', finishDrag, { signal: this.abortController.signal });
  }

  activateMedia(mediaId) {
    if (!mediaId) return;

    this.activeMediaId = String(mediaId);
    const selectedMedia = this.visibleMedia.find((media) => media.dataset.mediaId === String(mediaId));

    this.visibleMedia.forEach((media) => {
      if (media !== selectedMedia) media.querySelector('video')?.pause();
    });

    const video = selectedMedia?.querySelector('video');
    if (!video) return;

    const deferredSources = Array.from(video.querySelectorAll('source[data-floyd-src]'));

    deferredSources.forEach((source) => {
      source.src = source.dataset.floydSrc;
      delete source.dataset.floydSrc;
    });

    if (deferredSources.length > 0) video.load();

    const playWhenReady = () => {
      if (this.activeMediaId !== String(mediaId)) return;
      video.play().catch(() => {});
    };

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      playWhenReady();
    } else {
      video.addEventListener('canplay', playWhenReady, { once: true });
    }
  }

  syncMediaNavigation(mediaId) {
    if (!mediaId) return;

    this.visibleThumbnails.forEach((thumbnail) => {
      thumbnail.setAttribute('aria-current', thumbnail.dataset.mediaId === mediaId ? 'true' : 'false');
    });

    this.querySelectorAll('.page-dots > button[data-media-id]:not([hidden])').forEach((dot) => {
      dot.setAttribute('aria-current', dot.dataset.mediaId === mediaId ? 'true' : 'false');
    });
  }

  alignSelectedThumbnail(mediaId) {
    const scroller = this.productGallery?.querySelector('.product-gallery__thumbnail-scroller');
    const thumbnail = this.visibleThumbnails.find((item) => item.dataset.mediaId === mediaId);

    if (!scroller || !thumbnail) return;

    const targetLeft = thumbnail.offsetLeft - (scroller.clientWidth - thumbnail.clientWidth) / 2;
    const maximumScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);

    scroller.scrollTo({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      left: Math.min(Math.max(targetLeft, 0), maximumScroll),
    });
  }

  prepareVariantMediaOrder(gallery, selectedOptionValues, mediaMap, selectedVariantId) {
    const directAssignment = mediaMap?.variant_assignments?.[String(selectedVariantId)];
    const assignments = mediaMap?.legacy?.assignments ?? mediaMap?.assignments;
    const assignmentKey = selectedOptionValues.find((value) => Array.isArray(assignments?.[value]));
    const orderedMediaIds = (directAssignment?.length ? directAssignment : assignments?.[assignmentKey])?.map(String);

    if (!gallery || !orderedMediaIds?.length) return;

    const orderedMediaIdSet = new Set(orderedMediaIds);
    const reorder = (container, selector) => {
      if (!container) return;

      const elements = Array.from(container.querySelectorAll(`:scope > ${selector}`));
      const elementsByMediaId = new Map(elements.map((element) => [element.dataset.mediaId, element]));
      const orderedElements = orderedMediaIds.map((mediaId) => elementsByMediaId.get(mediaId)).filter(Boolean);
      const remainingElements = elements.filter((element) => !orderedMediaIdSet.has(element.dataset.mediaId));

      container.append(...orderedElements, ...remainingElements);
    };

    reorder(gallery?.querySelector('.product-gallery__carousel'), '.product-gallery__media');
    reorder(gallery?.querySelector('.product-gallery__thumbnail-scroller'), '.product-gallery__thumbnail');
    reorder(gallery?.querySelector('.page-dots'), 'button[data-media-id]');

    const totalMedia = gallery?.querySelectorAll('.product-gallery__media').length || 0;
    const filteredIndexes = Array.from(
      { length: Math.max(totalMedia - orderedMediaIds.length, 0) },
      (_, index) => orderedMediaIds.length + index + 1,
    );
    gallery?.setAttribute('filtered-indexes', JSON.stringify(filteredIndexes));
  }

  move(direction) {
    const thumbnailScroller = this.productGallery?.querySelector('.product-gallery__thumbnail-scroller');

    if (thumbnailScroller) {
      thumbnailScroller.scrollBy({
        behavior: 'smooth',
        left: direction * Math.max(thumbnailScroller.clientWidth * 0.75, 88),
      });
      return;
    }

    const thumbnails = this.visibleThumbnails;
    const currentIndex = Math.max(this.selectedThumbnailIndex, 0);
    const target = thumbnails[currentIndex + direction];

    target?.click();
    requestAnimationFrame(() => this.updateNavigation());
  }

  selectFirstVisibleMedia() {
    const firstThumbnail = this.visibleThumbnails[0];
    const carousel = this.productGallery?.querySelector('.product-gallery__carousel');

    if (!firstThumbnail || !carousel) return;

    carousel.select?.(0, { instant: true });
    this.activateMedia(firstThumbnail.dataset.mediaId);
    this.syncMediaNavigation(firstThumbnail.dataset.mediaId);
    requestAnimationFrame(() => this.updateNavigation());
  }

  updateNavigation() {
    const thumbnailScroller = this.productGallery?.querySelector('.product-gallery__thumbnail-scroller');

    if (thumbnailScroller) {
      const edgeTolerance = 2;
      const maximumScroll = Math.max(thumbnailScroller.scrollWidth - thumbnailScroller.clientWidth, 0);

      if (this.previousButton) {
        this.previousButton.disabled = maximumScroll <= edgeTolerance || thumbnailScroller.scrollLeft <= edgeTolerance;
      }

      if (this.nextButton) {
        this.nextButton.disabled = maximumScroll <= edgeTolerance
          || thumbnailScroller.scrollLeft >= maximumScroll - edgeTolerance;
      }
      return;
    }

    const thumbnails = this.visibleThumbnails;
    const currentIndex = this.selectedThumbnailIndex;
    const hasMultipleItems = thumbnails.length > 1;

    if (this.previousButton) {
      this.previousButton.disabled = !hasMultipleItems || currentIndex <= 0;
    }

    if (this.nextButton) {
      this.nextButton.disabled = !hasMultipleItems || currentIndex < 0 || currentIndex >= thumbnails.length - 1;
    }
  }

  initializeNavigation() {
    delete this.dataset.floydNavigationReady;

    requestAnimationFrame(() => {
      this.updateNavigation();
      this.dataset.floydNavigationReady = 'true';
    });
  }

  initializeMedia() {
    delete this.dataset.floydMediaReady;

    requestAnimationFrame(() => {
      this.selectFirstVisibleMedia();
      this.dataset.floydMediaReady = 'true';
    });
  }

  setup3dViewer() {
    this.viewer3d = this.querySelector('[data-floyd-3d-viewer]');
    if (!this.viewer3d) return;

    this.viewer3dButton = this.viewer3d.querySelector('[data-floyd-3d-open]');
    this.viewer3dDialog = this.viewer3d.querySelector('[data-floyd-3d-dialog]');
    this.viewer3dClose = this.viewer3d.querySelector('[data-floyd-3d-close]');
    this.viewer3dFrame = this.viewer3d.querySelector('[data-floyd-3d-frame]');
    this.viewer3dUrls = new Map();

    try {
      const payload = JSON.parse(this.viewer3d.querySelector('[data-floyd-3d-variants]').textContent);
      payload.variants.forEach((variant) => {
        if (variant.url) this.viewer3dUrls.set(String(variant.id), variant.url);
      });
    } catch (error) {
      console.error('Unable to read Floyd 3D variant configuration.', error);
    }

    this.update3dVariant(this.viewer3d.dataset.selectedVariantId);

    this.viewer3dButton?.addEventListener('click', () => this.open3dViewer(), {
      signal: this.abortController.signal,
    });
    this.viewer3dClose?.addEventListener('click', () => this.close3dViewer(), {
      signal: this.abortController.signal,
    });
    this.viewer3dDialog?.addEventListener('click', (event) => {
      if (event.target === this.viewer3dDialog) this.close3dViewer();
    }, { signal: this.abortController.signal });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.viewer3dDialog.hidden) this.close3dViewer();
    }, { signal: this.abortController.signal });

    const productForm = document.forms[this.dataset.productFormId];
    productForm?.addEventListener('variant:change', (event) => {
      this.update3dVariant(event.detail.variant?.id);
    }, { signal: this.abortController.signal });
  }

  update3dVariant(variantId) {
    this.viewer3dUrl = this.viewer3dUrls.get(String(variantId || '')) || '';

    if (this.viewer3dButton) this.viewer3dButton.hidden = !this.viewer3dUrl;

    if (!this.viewer3dUrl && this.viewer3dDialog && !this.viewer3dDialog.hidden) {
      this.close3dViewer();
    }
  }

  open3dViewer() {
    if (!this.viewer3dUrl) return;

    if (this.viewer3dFrame.src !== this.viewer3dUrl) {
      this.viewer3dFrame.src = this.viewer3dUrl;
    }

    this.viewer3dDialog.hidden = false;
    this.viewer3dDialog.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.viewer3dClose?.focus();
  }

  close3dViewer() {
    this.viewer3dDialog.hidden = true;
    this.viewer3dDialog.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.viewer3dButton?.focus();
  }
}

if (!customElements.get('floyd-product-gallery')) {
  customElements.define('floyd-product-gallery', FloydProductGallery);
}

customElements.whenDefined('product-gallery').then(() => {
  if (customElements.get('floyd-prestige-product-gallery')) return;

  const PrestigeProductGallery = customElements.get('product-gallery');
  customElements.define(
    'floyd-prestige-product-gallery',
    class FloydPrestigeProductGallery extends PrestigeProductGallery {},
  );
});
